<template>
  <div class="relative overflow-hidden shadow-lg sm:rounded-md">
    <!-- Header -->
    <div
      class="flex flex-col px-4 py-4 space-y-3 lg:flex-row lg:items-center lg:justify-between lg:space-y-0 lg:space-x-4"
    >
      <!-- Tabs (left) -->
      <nav
        class="flex items-center flex-1 gap-6"
        role="tablist"
        aria-label="Status"
      >
        <button
          type="button"
          :class="
            tab === 'all'
              ? 'text-brand border-b-2 border-brand'
              : 'text-rhino hover:text-mouse'
          "
          @click="tab = 'all'"
        >
          {{ $t('support/ticket_table/tab_all') }}
        </button>

        <button
          type="button"
          :class="
            tab === 'open'
              ? 'text-brand border-b-2 border-brand'
              : 'text-rhino hover:text-mouse'
          "
          @click="tab = 'open'"
        >
          {{ $t('support/ticket_table/tab_open') }}
        </button>

        <button
          type="button"
          :class="
            tab === 'closed'
              ? 'text-brand border-b-2 border-brand'
              : 'text-rhino hover:text-mouse'
          "
          @click="tab = 'closed'"
        >
          {{ $t('support/ticket_table/tab_closed') }}
        </button>

        <common-button
          outline
          size="custom"
          class="ml-4 px-8 py-1"
          @click="emit('write-ticket')"
        >
          {{ $t('support/ticket_table/write_ticket') }}
        </common-button>
      </nav>

      <!-- Search (right) -->
      <div
        class="flex flex-col flex-shrink-0 gap-3 md:flex-row md:items-center lg:justify-end md:gap-0"
      >
        <div class="relative">
          <label for="ticket-search" class="sr-only">
            {{ $t('support/ticket_table/search_label') }}
          </label>
          <input
            id="ticket-search"
            v-model="q"
            data-testid="support-ticket-search"
            type="search"
            :placeholder="$t('support/ticket_table/search_placeholder')"
            class="w-64 md:w-72 border-b border-rhino/50 text-mouse placeholder-rhino focus:outline-none py-2 pl-9 pr-3"
          />
          <svg
            class="w-4 h-4 text-rhino absolute left-3 top-1/2 -translate-y-1/2"
            viewBox="0 0 24 24"
            fill="currentColor"
            aria-hidden="true"
          >
            <path
              fill-rule="evenodd"
              d="M10 3.5a6.5 6.5 0 1 0 3.87 11.78l4.42 4.43a.75.75 0 1 0 1.06-1.06l-4.43-4.42A6.5 6.5 0 0 0 10 3.5Zm-5 6.5a5 5 0 1 1 10 0 5 5 0 0 1-10 0Z"
              clip-rule="evenodd"
            />
          </svg>
        </div>
      </div>
    </div>

    <!-- Table -->
    <div
      class="overflow-x-auto min-h-96 bg-gradient-to-br from-depth-2/5 to-depth-3/75"
    >
      <table class="w-full text-left text-mouse">
        <thead class="bg-depth-0/80 text-rhino">
          <tr>
            <th scope="col" class="p-4 min-w-[14rem]">
              <button
                type="button"
                class="inline-flex items-center gap-1 hover:text-mouse"
                @click="toggleSort('title')"
              >
                {{ $t('support/ticket_table/column_title') }}
                <svg
                  v-if="sort.key === 'title'"
                  class="w-3.5 h-3.5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path
                    :d="
                      sort.dir === 'asc'
                        ? 'M10 6l4 5H6l4-5z'
                        : 'M10 14l-4-5h8l-4 5z'
                    "
                  />
                </svg>
              </button>
            </th>
            <th scope="col" class="p-4">
              <button
                type="button"
                class="inline-flex items-center gap-1 hover:text-mouse"
                @click="toggleSort('category')"
              >
                {{ $t('support/ticket_table/column_category') }}
                <svg
                  v-if="sort.key === 'category'"
                  class="w-3.5 h-3.5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path
                    :d="
                      sort.dir === 'asc'
                        ? 'M10 6l4 5H6l4-5z'
                        : 'M10 14l-4-5h8l-4 5z'
                    "
                  />
                </svg>
              </button>
            </th>
            <th scope="col" class="p-4">
              <button
                type="button"
                class="inline-flex items-center gap-1 hover:text-mouse"
                @click="toggleSort('status')"
              >
                {{ $t('support/ticket_table/column_status') }}
                <svg
                  v-if="sort.key === 'status'"
                  class="w-3.5 h-3.5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path
                    :d="
                      sort.dir === 'asc'
                        ? 'M10 6l4 5H6l4-5z'
                        : 'M10 14l-4-5h8l-4 5z'
                    "
                  />
                </svg>
              </button>
            </th>
            <th scope="col" class="p-4">
              <button
                type="button"
                class="inline-flex items-center gap-1 hover:text-mouse"
                @click="toggleSort('created')"
              >
                {{ $t('support/ticket_table/column_created') }}
                <svg
                  v-if="sort.key === 'created'"
                  class="w-3.5 h-3.5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path
                    :d="
                      sort.dir === 'asc'
                        ? 'M10 6l4 5H6l4-5z'
                        : 'M10 14l-4-5h8l-4 5z'
                    "
                  />
                </svg>
              </button>
            </th>
            <!-- TODO: Re-enable once backend adds 'modified' or 'updated_at' field to Ticket proto -->
            <!-- <th scope="col" class="p-4">
              {{ $t('support/ticket_table/column_updated') }}
            </th> -->
          </tr>
        </thead>

        <tbody
          v-if="!loading && filtered.length"
          class="divide-y divide-depth-3"
        >
          <tr
            v-for="row in paged"
            :key="row.id"
            class="hover:bg-depth-3/40 transition cursor-pointer"
            data-testid="support-ticket-row"
            @click="onRowClick(row)"
          >
            <th
              scope="row"
              class="p-4 font-medium text-mouse whitespace-nowrap"
            >
              <div class="flex items-center gap-2">
                <span
                  class="inline-flex h-2.5 w-2.5 rounded-full"
                  :class="row.status !== 'closed' ? 'bg-success' : 'bg-rhino'"
                >
                </span>
                <span class="truncate max-w-[28rem]">
                  {{ row.title }}
                </span>
                <span
                  v-if="row.source === 'intercom'"
                  class="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-brand/10 text-brand"
                >
                  {{ $t('support/ticket_table/source_intercom') }}
                </span>
              </div>
            </th>
            <td class="p-4 text-mouse">
              {{ row.category.name }}
            </td>
            <td class="p-4">
              <span
                class="inline-flex items-center px-2 py-0.5 rounded-md font-medium"
                :class="
                  row.status !== 'closed'
                    ? 'bg-success/10 text-success'
                    : 'bg-rhino/10 text-rhino'
                "
                data-testid="support-ticket-row-status"
              >
                {{ $t(`support/ticket_status/${row.status}`) }}
              </span>
            </td>
            <td class="p-4 text-sm">
              {{ formatDateTime(row.created) }}
            </td>
            <!-- TODO: Re-enable once backend adds 'modified' or 'updated_at' field to Ticket proto -->
            <!-- <td class="p-4 text-sm">
              {{ row.modified ? formatDateTime(row.modified) : '—' }}
            </td> -->
          </tr>
        </tbody>

        <!-- Loading -->
        <tbody v-else-if="loading">
          <tr v-for="i in 3" :key="i" class="animate-pulse">
            <td class="p-4">
              <div class="h-4 w-64 bg-depth-3 rounded-md"></div>
            </td>
            <td class="p-4">
              <div class="h-4 w-16 bg-depth-3 rounded-md"></div>
            </td>
            <td class="p-4">
              <div class="h-5 w-16 bg-depth-3 rounded-md"></div>
            </td>
            <td class="p-4">
              <div class="h-4 w-32 bg-depth-3 rounded-md"></div>
            </td>
            <!-- TODO: Re-enable once backend adds 'modified' or 'updated_at' field to Ticket proto -->
            <!-- <td class="p-4">
              <div class="h-4 w-32 bg-depth-3 rounded-md"></div>
            </td> -->
          </tr>
        </tbody>

        <!-- Empty state -->
        <tbody v-else>
          <tr>
            <td colspan="5" class="px-6 py-16 text-center">
              <div class="flex flex-col items-center gap-2">
                <fa-icon :icon="faGhost" class="shrink-0 size-10 text-rhino" />
                <p class="text-rhino">{{ $t('support/ticket_table/empty') }}</p>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Footer / Pagination -->
    <div class="flex justify-between p-4 flex-row items-center gap-4">
      <span class="text-sm text-rhino">
        {{
          $t('support/ticket_table/pagination_showing', {
            from: pageStart + 1,
            to: pageEnd,
            total: filtered.length,
          })
        }}
      </span>

      <nav
        class="inline-flex items-center gap-2"
        role="navigation"
        :aria-label="$t('support/ticket_table/pagination_label')"
      >
        <!-- Prev -->
        <button
          type="button"
          class="size-9 grid place-items-center rounded-full bg-depth-0 text-mouse hover:bg-depth-3 focus-visible:outline focus-visible:outline-brand disabled:opacity-40 disabled:pointer-events-none"
          :disabled="page === 1 || totalPages === 0"
          :aria-label="$t('support/ticket_table/pagination_prev')"
          @click="page--"
        >
          <fa-icon :icon="faChevronLeft" class="size-4" />
        </button>

        <!-- Page numbers -->
        <template v-for="(p, i) in visiblePages" :key="`${p}-${i}`">
          <!-- Ellipsis -->
          <span
            v-if="p === '…'"
            class="px-2 text-rhino select-none"
            aria-hidden="true"
            >…</span
          >

          <!-- Number -->
          <button
            v-else
            type="button"
            class="[font-variant-numeric:tabular-nums] focus-visible:outline focus-visible:outline-brand rounded-full inline-grid place-items-center"
            :class="[
              String(p).length <= 2 ? 'size-9' : 'h-9 px-3',
              p === page
                ? 'bg-brand text-onyx font-semibold cursor-default'
                : 'bg-depth-0 text-mouse hover:bg-depth-3',
            ]"
            :aria-current="p === page ? 'page' : undefined"
            :aria-label="
              $t('support/ticket_table/pagination_page', { page: p })
            "
            @click="p !== page && (page = p)"
          >
            {{ p }}
          </button>
        </template>

        <!-- Next -->
        <button
          type="button"
          class="size-9 grid place-items-center rounded-full bg-depth-0 text-mouse hover:bg-depth-3 focus-visible:outline focus-visible:outline-brand disabled:opacity-40 disabled:pointer-events-none"
          :disabled="page === totalPages || totalPages === 0"
          :aria-label="$t('support/ticket_table/pagination_next')"
          @click="page++"
        >
          <fa-icon :icon="faChevronRight" class="size-4" />
        </button>
      </nav>
    </div>
  </div>
</template>

<script setup lang="ts">
import useDateTime from '@/composables/useDateTime';
import {
  faChevronLeft,
  faChevronRight,
  faGhost,
} from '@fortawesome/pro-solid-svg-icons';
import type { Ticket } from '~/store/support';

const { t } = useI18n();

// DateTime formatting
const { formatFn: formatDateTime } = useDateTime();

// Props
const props = withDefaults(
  defineProps<{
    items: readonly Ticket[];
    loading?: boolean;
  }>(),
  {
    loading: false,
  },
);

const emit = defineEmits<{
  (e: 'row-click', row: Ticket): void;
  (e: 'write-ticket'): void;
}>();

// Search + tabs
const q = ref('');
const tab = ref<'all' | 'open' | 'closed'>('all');

// Sorting
type SortKey = 'title' | 'category' | 'status' | 'created';

const sort = ref<{
  key: SortKey;
  dir: 'asc' | 'desc';
}>({
  key: 'created',
  dir: 'desc',
});

const toggleSort = (key: SortKey) => {
  if (sort.value.key === key) {
    sort.value.dir = sort.value.dir === 'asc' ? 'desc' : 'asc';
  } else {
    sort.value.key = key;
    sort.value.dir = 'asc';
  }
};

// Filtered and sorted data
const filtered = computed(() => {
  const base = props.items || [];

  // Filter by search query
  const byQuery = q.value.trim()
    ? base.filter((r) =>
        r.title?.toLowerCase().includes(q.value.trim().toLowerCase()),
      )
    : base;

  // Filter by tab
  let byTab = byQuery;
  if (tab.value === 'open') {
    byTab = byQuery.filter((r) => r.status !== 'closed');
  } else if (tab.value === 'closed') {
    byTab = byQuery.filter((r) => r.status === 'closed');
  }

  // Sort
  const sorted = [...byTab].sort((a, b) => {
    const k = sort.value.key;

    // Sort by status code - use alphabetical for enum strings
    if (k === 'status') {
      const av = a.status;
      const bv = b.status;
      const cmp = av.localeCompare(bv);
      return sort.value.dir === 'asc' ? cmp : -cmp;
    }

    // Sort by category name
    if (k === 'category') {
      const av = a.category.name || '';
      const bv = b.category.name || '';
      const cmp = av.localeCompare(bv);
      return sort.value.dir === 'asc' ? cmp : -cmp;
    }

    // Sort by created date
    if (k === 'created') {
      const ad = new Date(a.created || 0).getTime();
      const bd = new Date(b.created || 0).getTime();
      return sort.value.dir === 'asc' ? ad - bd : bd - ad;
    }

    // Sort by other string fields
    const av = String((a as Ticket)[k] || '');
    const bv = String((b as Ticket)[k] || '');
    const cmp = av.localeCompare(bv);
    return sort.value.dir === 'asc' ? cmp : -cmp;
  });

  return sorted;
});

// Pagination
const page = ref(1);
const pageSize = ref(10);

const totalPages = computed(() =>
  Math.max(1, Math.ceil(filtered.value.length / pageSize.value)),
);

const paged = computed(() =>
  filtered.value.slice(
    (page.value - 1) * pageSize.value,
    page.value * pageSize.value,
  ),
);

const pageStart = computed(() =>
  Math.min((page.value - 1) * pageSize.value, filtered.value.length),
);

const pageEnd = computed(() =>
  Math.min(page.value * pageSize.value, filtered.value.length),
);

// Reset page when filters change
watch([filtered, pageSize], () => {
  page.value = 1;
});

// Row click handler
const onRowClick = (row: Ticket) => {
  emit('row-click', row);
};

// Pagination window tuning
const MIN_FULL = 5; // show all pages when total <= MIN_FULL
const EDGE_SPAN = 3; // when near edges, how many to display before ellipsis
const NEIGHBOR = 1; // numbers shown next to current on each side when in the middle

const range = (start: number, end: number) =>
  Array.from({ length: end - start + 1 }, (_, i) => start + i);

// Visible pages with single-neighbor ellipses
const visiblePages = computed<(number | '…')[]>(() => {
  const total = totalPages.value;
  const current = page.value;

  if (total <= MIN_FULL) return range(1, total);

  if (current <= EDGE_SPAN - 1) {
    return [...range(1, EDGE_SPAN), '…', total];
  }

  if (current >= total - (EDGE_SPAN - 2)) {
    return [1, '…', ...range(total - (EDGE_SPAN - 1), total)];
  }

  const startMid = Math.max(2, current - NEIGHBOR);
  const endMid = Math.min(total - 1, current + NEIGHBOR);
  return [1, '…', ...range(startMid, endMid), '…', total];
});
</script>
